import { useState } from "react";
import { Sheet } from "../../components/Sheet";
import { Section } from "../../components/AppShell";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { Button } from "../../components/ui";
import { useToast } from "../../components/Toast";
import {
  useAccessMembers,
  useCreateAccessInvite,
  useMe,
  useRemoveAccessMember,
} from "../../api/queries";
import { messagesOf } from "../../api/errors";
import { useT } from "../../i18n/strings";
import { openTelegram, shareToChat } from "../../telegram/webapp";
import s from "./access.module.css";

function memberLabel(
  row: { display_name?: string | null; username?: string | null },
  fallback: string,
) {
  const name = (row.display_name || "").trim();
  if (name) return name;
  const handle = (row.username || "").trim();
  if (handle) return `@${handle}`;
  return fallback;
}

export function AccessSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t = useT();
  const toast = useToast();
  const me = useMe();
  const access = me.data?.access;
  const members = useAccessMembers(Boolean(access?.can_manage_access && open));
  const removeAccess = useRemoveAccessMember();
  const invite = useCreateAccessInvite();
  const [admitLink, setAdmitLink] = useState<string | null>(null);
  const [adminLink, setAdminLink] = useState<string | null>(null);
  const [pendingRemoveId, setPendingRemoveId] = useState<number | null>(null);

  if (!access?.can_manage_access) return null;

  const linkBox = (link: string | null, label: string, shareText: string) =>
    link && (
      <div className={s.box}>
        <p className={s.meta}>{label}</p>
        <p className={s.link}>{link}</p>
        <div className={s.row}>
          <Button
            icon="copy"
            onClick={() => {
              void navigator.clipboard.writeText(link);
              toast.show(t.share.copied);
            }}
          >
            {t.share.copy}
          </Button>
          <Button icon="share" onClick={() => shareToChat(link, shareText)}>
            {t.share.send}
          </Button>
          <Button icon="openLink" onClick={() => openTelegram(link)}>
            {t.share.open}
          </Button>
        </div>
      </div>
    );

  return (
    <>
      <Sheet open={open} onClose={onClose} title={t.access.title}>
        <div className={s.panel}>
          <Section title={t.access.title} icon="maker">
            <p className={s.meta}>{t.access.hint}</p>
            {members.data?.map((row) => (
              <div key={row.telegram_id} className={s.memberRow}>
                <div>
                  <p className={s.name}>{memberLabel(row, t.access.anonymousMember)}</p>
                  <p className={s.meta}>
                    {row.role === "admin" ? t.access.roleAdmin : t.access.roleUser}
                    {row.display_name && row.username ? ` · @${row.username}` : ""}
                  </p>
                </div>
                {(access.role === "super" || row.role === "user") && (
                  <Button
                    variant="ghost"
                    icon="delete"
                    disabled={removeAccess.isPending}
                    onClick={() => setPendingRemoveId(row.telegram_id)}
                  >
                    {t.actions.remove}
                  </Button>
                )}
              </div>
            ))}
            {!members.isPending && (members.data?.length ?? 0) === 0 && (
              <p className={s.meta}>{t.access.noMembers}</p>
            )}

            {access.can_mint_admit && (
              <div className={s.inviteKind}>
                <p className={s.inviteKindTitle}>{t.access.admitTitle}</p>
                <p className={s.meta}>{t.access.admitHint}</p>
                <Button
                  icon="share"
                  disabled={invite.isPending}
                  onClick={() =>
                    invite.mutate("admit", {
                      onSuccess: (data) => {
                        setAdmitLink(data.link);
                        toast.show(t.access.admitInviteCreated);
                      },
                    })
                  }
                >
                  {t.access.admitInviteCreate}
                </Button>
                {linkBox(admitLink, t.access.admitLinkLabel, t.access.admitShareText)}
              </div>
            )}

            {access.can_mint_admin && (
              <div className={s.inviteKind}>
                <p className={s.inviteKindTitle}>{t.access.adminTitle}</p>
                <p className={s.meta}>{t.access.adminHint}</p>
                <Button
                  icon="share"
                  disabled={invite.isPending}
                  onClick={() =>
                    invite.mutate("admin", {
                      onSuccess: (data) => {
                        setAdminLink(data.link);
                        toast.show(t.access.adminInviteCreated);
                      },
                    })
                  }
                >
                  {t.access.adminInviteCreate}
                </Button>
                {linkBox(adminLink, t.access.adminLinkLabel, t.access.adminShareText)}
              </div>
            )}
          </Section>
        </div>
      </Sheet>
      <ConfirmDialog
        open={pendingRemoveId != null}
        title={t.access.removeTitle}
        body={t.access.removeBody}
        confirmLabel={t.access.removeConfirm}
        onConfirm={() => {
          if (pendingRemoveId == null) return;
          removeAccess.mutate(pendingRemoveId, {
            onSuccess: () => {
              setPendingRemoveId(null);
              toast.show(t.access.memberRemoved);
            },
            onError: (error) =>
              toast.show(messagesOf(error)[0] ?? t.errors.title, { tone: "error" }),
          });
        }}
        onCancel={() => setPendingRemoveId(null)}
      />
    </>
  );
}
